import { useCallback } from 'react';
import { LocationData, InstalledChiller, InstalledHeatRejection, InstalledCrah } from '../types';
import {
  CHILLER_TYPES,
  HEAT_REJECTION_LEVELS,
  CRAH_LEVELS,
  LOCATION_CLIMATE_FACTORS,
  DEFAULT_CLIMATE_FACTOR,
} from '../constants';
import { calculateLocationGrossPower } from '../utils/powerCalculations';

interface UseCoolingManagementProps {
  locations: Record<string, LocationData>;
  setLocations: React.Dispatch<React.SetStateAction<Record<string, LocationData>>>;
  money: number;
  setMoney: React.Dispatch<React.SetStateAction<number>>;
  triggerNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
  powerGrids: Record<string, any>;
  getFloorCoolingEfficiency?: (floorId: string, locationId: string) => number;
}

/** Climate free-cooling factor for a location (>1 = colder, more effective heat rejection). */
export const climateFactorFor = (locationId: string): number =>
  LOCATION_CLIMATE_FACTORS[locationId] ?? DEFAULT_CLIMATE_FACTOR;

/** Sum of installed chiller cooling output (efficiency-adjusted). */
export const chillerOutputKW = (location: LocationData): number => {
  let total = 0;
  location.chillers.forEach(chiller => {
    const type = CHILLER_TYPES.find(t => t.id === chiller.typeId);
    const level = type?.levels.find(l => l.level === chiller.level);
    if (level) total += level.coolingOutputKW * level.efficiency;
  });
  return total;
};

/** Per-stage cooling capacities for a location (kW). */
export const coolingChainStages = (location: LocationData, locationId: string) => {
  const climate = climateFactorFor(locationId);
  const heatRejectionKW = location.heatRejection?.installed
    ? location.heatRejection.capacityKW * climate
    : 0;
  const chillerKW = chillerOutputKW(location);
  const crahKW = location.crah?.installed ? location.crah.capacityKW : 0;
  // Effective delivered cooling is bottlenecked by the weakest stage.
  const effectiveKW = Math.min(heatRejectionKW, chillerKW, crahKW);
  return { heatRejectionKW, chillerKW, crahKW, effectiveKW, climate };
};

export const useCoolingManagement = ({
  locations,
  setLocations,
  money,
  setMoney,
  triggerNotification,
  powerGrids,
  getFloorCoolingEfficiency = () => 1.0,
}: UseCoolingManagementProps) => {

  // Effective cooling capacity (min of the 3 chain stages) + used cooling for a location
  const calculateLocationCoolingCapacity = (location: LocationData, locationId: string) => {
    const { effectiveKW } = coolingChainStages(location, locationId);
    const totalCooling = effectiveKW;

    let usedCooling = 0;
    Object.entries(location.floors).forEach(([floorId, floor]) => {
      const floorCoolingBonus = getFloorCoolingEfficiency(floorId, locationId) || 1.0;
      const coolingMultiplier = floorCoolingBonus > 0 ? (1 / floorCoolingBonus) : 1.0;
      floor.racks.forEach(rack => {
        rack.servers?.forEach(server => {
          usedCooling += (server.coolingNeeded || 0) * coolingMultiplier;
        });
      });
    });

    return { totalCooling, usedCooling };
  };

  // Shared: verify the power chain is complete before any cooling equipment
  const powerChainReady = (location: LocationData): string | null => {
    if (!location.powerGridId) return 'You need to connect to a power grid first';
    if (!location.transformer) return 'You need to install a transformer first';
    if (!location.powerhouse?.installed) return 'You need to install power distribution (MDB) first';
    if (!location.ups?.installed) return 'You need to install a UPS first';
    if (!location.selectedPowerSupplierId) return 'You need to select a power supplier first';
    return null;
  };

  // Apply a power-draw delta and recompute the location's cooling + power figures
  const commitLocation = (
    prev: Record<string, LocationData>,
    locationId: string,
    mutate: (loc: LocationData) => LocationData,
    powerDeltaKW: number,
  ): Record<string, LocationData> => {
    const base = prev[locationId];
    if (!base) return prev;
    const updated = mutate({ ...base });
    updated.usedPower = (updated.usedPower || 0) + powerDeltaKW;

    const { totalCooling, usedCooling } = calculateLocationCoolingCapacity(updated, locationId);
    updated.totalCoolingCapacityKW = totalCooling;
    updated.usedCoolingCapacityKW = usedCooling;
    updated.coolingCapacity = totalCooling;

    const grossPower = calculateLocationGrossPower(updated, powerGrids);
    updated.grossPowerKW = grossPower;
    updated.availablePowerKW = grossPower - updated.usedPower;

    return { ...prev, [locationId]: updated };
  };

  // ── Heat Rejection (stage 1) ────────────────────────────────────────────────
  const installHeatRejection = (locationId: string) => {
    const location = locations[locationId];
    if (!location) { triggerNotification('Location not found', 'error'); return; }
    const blocked = powerChainReady(location);
    if (blocked) { triggerNotification(blocked, 'warning'); return; }
    if (location.heatRejection?.installed) { triggerNotification('Heat rejection already installed — upgrade it instead.', 'info'); return; }

    const spec = HEAT_REJECTION_LEVELS[1];
    if (money < spec.cost) { triggerNotification(`Not enough money. Need $${spec.cost.toLocaleString()}.`, 'error'); return; }
    if (location.availablePowerKW < spec.powerDrawKW) {
      triggerNotification(`Not enough power capacity. Need ${spec.powerDrawKW}kW.`, 'error'); return;
    }

    setMoney(p => p - spec.cost);
    setLocations(prev => commitLocation(prev, locationId, loc => ({
      ...loc,
      heatRejection: { installed: true, level: 1, capacityKW: spec.capacityKW, powerDrawKW: spec.powerDrawKW } as InstalledHeatRejection,
    }), spec.powerDrawKW));
    const climate = climateFactorFor(locationId);
    triggerNotification(`Heat rejection installed (+${Math.round(spec.capacityKW * climate)}kW effective at this climate).`, 'success');
  };

  const upgradeHeatRejection = (locationId: string) => {
    const location = locations[locationId];
    if (!location?.heatRejection?.installed) { triggerNotification('Install heat rejection first.', 'warning'); return; }
    const next = HEAT_REJECTION_LEVELS[location.heatRejection.level + 1];
    if (!next) { triggerNotification('Heat rejection already at maximum level.', 'warning'); return; }
    if (money < next.cost) { triggerNotification(`Not enough money. Need $${next.cost.toLocaleString()}.`, 'error'); return; }
    const powerDelta = next.powerDrawKW - location.heatRejection.powerDrawKW;
    if (location.availablePowerKW < powerDelta) { triggerNotification(`Not enough power capacity. Need ${powerDelta.toFixed(1)}kW more.`, 'error'); return; }

    setMoney(p => p - next.cost);
    setLocations(prev => commitLocation(prev, locationId, loc => ({
      ...loc,
      heatRejection: { installed: true, level: next.level, capacityKW: next.capacityKW, powerDrawKW: next.powerDrawKW } as InstalledHeatRejection,
    }), powerDelta));
    triggerNotification(`Heat rejection upgraded to level ${next.level}.`, 'success');
  };

  // ── Chiller plant (stage 2) ─────────────────────────────────────────────────
  const buyChiller = (locationId: string, chillerTypeId: string) => {
    const location = locations[locationId];
    if (!location) { triggerNotification('Location not found', 'error'); return; }
    const blocked = powerChainReady(location);
    if (blocked) { triggerNotification(blocked, 'warning'); return; }
    if (!location.heatRejection?.installed) {
      triggerNotification('Install heat rejection (cooling towers) before adding chillers.', 'warning'); return;
    }

    const chillerType = CHILLER_TYPES.find(t => t.id === chillerTypeId);
    if (!chillerType) { triggerNotification('Chiller type not found', 'error'); return; }
    const level1 = chillerType.levels[0];
    if (money < level1.cost) { triggerNotification('Not enough money to purchase this chiller', 'error'); return; }
    if (location.availablePowerKW < level1.powerConsumptionKW) {
      triggerNotification(`Not enough power capacity. Need ${level1.powerConsumptionKW}kW.`, 'error'); return;
    }

    const newChiller: InstalledChiller = { id: `chiller-${locationId}-${Date.now()}`, typeId: chillerTypeId, level: 1 };
    setMoney(p => p - level1.cost);
    setLocations(prev => commitLocation(prev, locationId, loc => ({
      ...loc,
      chillers: [...loc.chillers, newChiller],
    }), level1.powerConsumptionKW));
    triggerNotification(`Purchased ${chillerType.name} (+${level1.coolingOutputKW}kW chiller output).`, 'success');
  };

  const upgradeChiller = (locationId: string, installChillerId: string) => {
    const location = locations[locationId];
    if (!location) { triggerNotification('Location not found', 'error'); return; }
    const idx = location.chillers.findIndex(c => c.id === installChillerId);
    if (idx === -1) { triggerNotification('Chiller not found', 'error'); return; }
    const chiller = location.chillers[idx];
    const chillerType = CHILLER_TYPES.find(t => t.id === chiller.typeId);
    if (!chillerType) { triggerNotification('Chiller type not found', 'error'); return; }
    const nextLevel = chillerType.levels.find(l => l.level === chiller.level + 1);
    const currentLevel = chillerType.levels.find(l => l.level === chiller.level);
    if (!nextLevel || !currentLevel) { triggerNotification('Chiller is already at maximum level', 'warning'); return; }
    if (money < nextLevel.cost) { triggerNotification('Not enough money to upgrade this chiller', 'error'); return; }
    const powerDelta = nextLevel.powerConsumptionKW - currentLevel.powerConsumptionKW;
    if (location.availablePowerKW < powerDelta) { triggerNotification(`Not enough power capacity. Need ${powerDelta.toFixed(1)}kW more.`, 'error'); return; }

    setMoney(p => p - nextLevel.cost);
    setLocations(prev => commitLocation(prev, locationId, loc => {
      const chillers = [...loc.chillers];
      chillers[idx] = { ...chillers[idx], level: nextLevel.level };
      return { ...loc, chillers };
    }, powerDelta));
    triggerNotification(`Upgraded ${chillerType.name} to level ${nextLevel.level}.`, 'success');
  };

  // ── CRAH air handlers (stage 3) ─────────────────────────────────────────────
  const installCrah = (locationId: string) => {
    const location = locations[locationId];
    if (!location) { triggerNotification('Location not found', 'error'); return; }
    if (location.chillers.length === 0) {
      triggerNotification('Install at least one chiller before adding CRAH air handlers.', 'warning'); return;
    }
    if (location.crah?.installed) { triggerNotification('CRAH already installed — upgrade it instead.', 'info'); return; }

    const spec = CRAH_LEVELS[1];
    if (money < spec.cost) { triggerNotification(`Not enough money. Need $${spec.cost.toLocaleString()}.`, 'error'); return; }
    if (location.availablePowerKW < spec.powerDrawKW) { triggerNotification(`Not enough power capacity. Need ${spec.powerDrawKW}kW.`, 'error'); return; }

    setMoney(p => p - spec.cost);
    setLocations(prev => commitLocation(prev, locationId, loc => ({
      ...loc,
      crah: { installed: true, level: 1, capacityKW: spec.capacityKW, powerDrawKW: spec.powerDrawKW } as InstalledCrah,
    }), spec.powerDrawKW));
    triggerNotification(`CRAH air handlers installed (+${spec.capacityKW}kW air delivery).`, 'success');
  };

  const upgradeCrah = (locationId: string) => {
    const location = locations[locationId];
    if (!location?.crah?.installed) { triggerNotification('Install CRAH first.', 'warning'); return; }
    const next = CRAH_LEVELS[location.crah.level + 1];
    if (!next) { triggerNotification('CRAH already at maximum level.', 'warning'); return; }
    if (money < next.cost) { triggerNotification(`Not enough money. Need $${next.cost.toLocaleString()}.`, 'error'); return; }
    const powerDelta = next.powerDrawKW - location.crah.powerDrawKW;
    if (location.availablePowerKW < powerDelta) { triggerNotification(`Not enough power capacity. Need ${powerDelta.toFixed(1)}kW more.`, 'error'); return; }

    setMoney(p => p - next.cost);
    setLocations(prev => commitLocation(prev, locationId, loc => ({
      ...loc,
      crah: { installed: true, level: next.level, capacityKW: next.capacityKW, powerDrawKW: next.powerDrawKW } as InstalledCrah,
    }), powerDelta));
    triggerNotification(`CRAH upgraded to level ${next.level}.`, 'success');
  };

  // Recompute cooling capacities for all locations (e.g. after floor-quality change)
  const updateCoolingCapacities = useCallback(() => {
    setLocations(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(locationId => {
        const location = updated[locationId];
        const { totalCooling, usedCooling } = calculateLocationCoolingCapacity(location, locationId);
        updated[locationId] = { ...location, totalCoolingCapacityKW: totalCooling, usedCoolingCapacityKW: usedCooling, coolingCapacity: totalCooling };
      });
      return updated;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setLocations]);

  return {
    buyChiller,
    upgradeChiller,
    installHeatRejection,
    upgradeHeatRejection,
    installCrah,
    upgradeCrah,
    calculateLocationCoolingCapacity,
    updateCoolingCapacities,
  };
};
